"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";
import { Store, ChevronsUpDown, LogOut, ChevronRight, PanelLeft } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";


export interface FooterMenuItem {
  label: string;
  icon: LucideIcon;
  action: () => void;
}

export interface NavSubItem {
  href: string;
  label: string;
}

export interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  children?: NavSubItem[];
  badge?: React.ReactNode;
  activeMatch?: (pathname: string) => boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export interface BottomLink {
  /** href requerido como key/fallback. Si se provee `onClick`, no se navega
   *  por href — el link se renderea como botón con la acción. */
  href: string;
  label: string;
  icon: LucideIcon;
  external?: boolean;
  /** Si se provee, el item se renderea como botón con esta acción en vez
   *  de como anchor. Útil para abrir popups (ej. compartir tienda). */
  onClick?: () => void;
}

interface AppShellProps {
  children: React.ReactNode;
  navGroups: NavGroup[];
  brandSubtitle: string;
  basePath: string;
  loginPath: string;
  skipLayoutPaths?: string[];
  footerMenuItems?: FooterMenuItem[];
  topLinks?: BottomLink[];
  bottomLinks?: BottomLink[];
  brandName?: string;
  brandLogoUrl?: string | null;
  sessionRole?: "admin" | "merchant";
  contentMaxWidth?: string;
}

function DesktopSidebarTrigger() {
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="icon"
      className="hidden md:inline-flex -ml-1 size-7"
      onClick={toggleSidebar}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

function CollapsibleNavItem({
  item,
  isActive,
  pathname,
  onNavClick,
}: {
  item: NavItem;
  isActive: (href: string) => boolean;
  pathname: string;
  onNavClick: () => void;
}) {
  const { state, isMobile } = useSidebar();
  const isCollapsed = state === "collapsed" && !isMobile;

  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              tooltip={item.label}
              isActive={isActive(item.href)}
            >
              <item.icon />
              <span>{item.label}</span>
              <ChevronRight className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="start"
            className="min-w-48"
          >
            {item.children!.map((sub) => (
              <DropdownMenuItem key={sub.href} asChild>
                <Link href={sub.href} onClick={() => {
                  onNavClick();
                  if (pathname === sub.href) window.dispatchEvent(new Event("sidebar-nav-reset"));
                }}>
                  <span>{sub.label}</span>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible
      asChild
      defaultOpen={isActive(item.href)}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.label}
            isActive={isActive(item.href)}
          >
            <item.icon />
            <span>{item.label}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children!.map((sub) => (
              <SidebarMenuSubItem key={sub.href}>
                <SidebarMenuSubButton
                  asChild
                  isActive={pathname === sub.href}
                >
                  <Link href={sub.href} onClick={() => {
                    onNavClick();
                    if (pathname === sub.href) window.dispatchEvent(new Event("sidebar-nav-reset"));
                  }}>
                    <span>{sub.label}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SidebarNavContent({
  navGroups,
  isActive,
  pathname,
}: {
  navGroups: NavGroup[];
  isActive: (href: string) => boolean;
  pathname: string;
}) {
  const { setOpenMobile } = useSidebar();

  function handleNavClick() {
    setOpenMobile(false);
  }

  return (
    <SidebarContent>
      {navGroups.map((group, i) => (
        <SidebarGroup key={group.label} className={i > 0 ? "pt-0" : undefined}>
          <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {group.items.map((item) =>
                item.children ? (
                  <CollapsibleNavItem
                    key={item.href}
                    item={item}
                    isActive={isActive}
                    pathname={pathname}
                    onNavClick={handleNavClick}
                  />
                ) : (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.href)}
                    >
                      <Link href={item.href} onClick={handleNavClick}>
                        <item.icon />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                    {item.badge != null && (
                      <SidebarMenuBadge>{item.badge}</SidebarMenuBadge>
                    )}
                  </SidebarMenuItem>
                )
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </SidebarContent>
  );
}

export function AppShell({
  children,
  navGroups,
  brandSubtitle,
  basePath,
  loginPath,
  skipLayoutPaths = [],
  footerMenuItems = [],
  topLinks,
  bottomLinks,
  brandName,
  brandLogoUrl,
  sessionRole = "admin",
  contentMaxWidth = "max-w-4xl",
}: AppShellProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [session, setSession] = useState<{
    email: string;
    name?: string;
    merchantId?: number;
  } | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Refetch session when navigating to a non-public path so that after
  // login (which lands on "/" from "/login") we pick up the new session.
  useEffect(() => {
    if (session) return;
    fetch(`/api/auth/session?role=${sessionRole}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data?.email) setSession(data); })
      .catch(() => {});
  }, [pathname, sessionRole, session]);

  // Use suffix matching to be robust to middleware subdomain rewrites
  // (e.g. "/login" on the subdomain gets internally rewritten to "/comercio/login",
  // so usePathname() can differ between SSR and client hydration).
  const allSkipPaths = [loginPath, ...skipLayoutPaths];
  const pathMatches = (full: string) =>
    allSkipPaths.some((p) => full === p || full.endsWith(p) || p.endsWith(full));
  if (pathMatches(pathname)) {
    return <>{children}</>;
  }

  if (!mounted) {
    return null;
  }

  function isActive(href: string) {
    const item = navGroups.flatMap((g) => g.items).find((i) => i.href === href);
    if (item?.activeMatch) return item.activeMatch(pathname);
    if (href === basePath) return pathname === basePath;
    return pathname.startsWith(href);
  }

  const displayName = session?.name || session?.email || "...";
  const initials = displayName && displayName !== "..."
    ? displayName.substring(0, 2).toUpperCase()
    : brandSubtitle.substring(0, 2).toUpperCase();

  async function handleLogout() {
    await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: sessionRole }),
    });
    router.push(loginPath);
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "calc(var(--spacing) * 72)",
        } as React.CSSProperties
      }
    >
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          {/* Mobile: user info at top */}
          <SidebarMenu className="md:hidden">
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent"
                  >
                    <Avatar className="size-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {brandSubtitle}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {displayName}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
                  side="bottom"
                  align="start"
                  sideOffset={4}
                >
                  {topLinks?.map((link) =>
                    link.onClick ? (
                      <DropdownMenuItem key={link.href} onClick={link.onClick}>
                        <link.icon className="mr-2 size-4" />
                        {link.label}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem key={link.href} asChild>
                        <a href={link.href} {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                          <link.icon className="mr-2 size-4" />
                          {link.label}
                        </a>
                      </DropdownMenuItem>
                    ),
                  )}
                  {footerMenuItems.map((item) => (
                    <DropdownMenuItem key={item.label} onClick={item.action}>
                      <item.icon className="mr-2 size-4" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  {bottomLinks?.map((link) => (
                    <DropdownMenuItem key={link.href} asChild>
                      <a href={link.href} {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                        <link.icon className="mr-2 size-4" />
                        {link.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 size-4" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          {/* Desktop: brand logo + name */}
          <SidebarMenu className="hidden md:flex">
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link href={basePath}>
                  {brandLogoUrl ? (
                    <Image
                      src={brandLogoUrl}
                      alt={brandName || brandSubtitle}
                      width={32}
                      height={32}
                      className="size-8 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      <Store className="size-4" />
                    </div>
                  )}
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-semibold truncate">
                      {brandName || "Pedile"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {brandSubtitle}
                    </span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        {topLinks && topLinks.length > 0 && (
          <div className="px-3 pt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            {topLinks.map((link) =>
              link.onClick ? (
                <button
                  key={link.href}
                  type="button"
                  onClick={link.onClick}
                  className="inline-flex items-center justify-center gap-1.5 py-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <link.icon className="size-3.5" />
                  <span>{link.label}</span>
                </button>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="inline-flex items-center justify-center gap-1.5 py-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <link.icon className="size-3.5" />
                  <span>{link.label}</span>
                </a>
              ),
            )}
          </div>
        )}

        <SidebarNavContent
          navGroups={navGroups}
          isActive={isActive}
          pathname={pathname}
        />

        {bottomLinks && bottomLinks.length > 0 && (
          <div className="mt-auto px-3 pb-2">
            {bottomLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="flex items-center justify-center gap-1.5 py-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors"
              >
                <link.icon className="size-3.5" />
                <span>{link.label}</span>
              </a>
            ))}
          </div>
        )}

        <SidebarFooter className="hidden md:block">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent"
                  >
                    <Avatar className="size-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {brandSubtitle}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {displayName}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
                  side="top"
                  align="start"
                  sideOffset={4}
                >
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
                    <Avatar className="size-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {brandSubtitle}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {displayName}
                      </span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  {footerMenuItems.map((item) => (
                    <DropdownMenuItem key={item.label} onClick={item.action}>
                      <item.icon className="mr-2 size-4" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  {bottomLinks?.map((link) => (
                    <DropdownMenuItem key={link.href} asChild>
                      <a href={link.href} {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                        <link.icon className="mr-2 size-4" />
                        {link.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 size-4" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        <header className="sticky top-0 z-20 flex items-center gap-3 px-3 py-3 bg-background/95 backdrop-blur border-b md:border-b-0 md:bg-transparent md:backdrop-blur-none">
          <SidebarTrigger className="-ml-1 size-11 [&_svg]:!size-6 md:hidden" />
          <DesktopSidebarTrigger />
          <div className="flex items-center gap-3 md:hidden">
            {brandLogoUrl ? (
              <Image
                src={brandLogoUrl}
                alt={brandName || brandSubtitle}
                width={28}
                height={28}
                className="size-7 rounded-lg object-cover"
              />
            ) : null}
            {brandName ? (
              <span className="font-semibold text-base truncate">
                {brandName}
              </span>
            ) : null}
          </div>
        </header>
        <main className="flex-1 px-3 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
          <div className={`mx-auto ${contentMaxWidth} min-w-0`}>{children}</div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
